/* ts-gift-offer.js — Gift-with-purchase offers + auto coupon for Fastrr (Shiprocket) checkout.
 *
 * Offers are managed in Shopify admin: Content > Metaobjects > Gift offer.
 * snippets/ts-gift-offer.liquid prints the Active entries as window.TS_GIFT_OFFERS.
 *
 * Rules
 *  - A cart line qualifies for an offer when its product is in one of the offer's
 *    collections or is listed in the offer's products.
 *  - Only ONE offer applies per order (Fastrr takes one coupon): the qualifying offer with the
 *    lowest priority number whose gift is in stock.
 *  - That offer's gift line(s) are added with properties _ts_gift (= coupon code) and
 *    _ts_gift_label, and its coupon is applied (Shopify discount_code cookie, which Fastrr reads).
 *  - Gift lines of offers that no longer apply are removed and our coupon is cleared.
 *  - Fastrr product-page Buy Now normally sends only the selected item; for qualifying items we
 *    open Fastrr's direct checkout with [item, gift(s)] and the coupon instead.
 *  - Fastrr cart / mini-cart buttons wait for the cart to be in sync before opening checkout.
 *
 * The gift is charged at its normal price; the coupon (gift price off the qualifying products)
 * cancels it out. Keep each coupon's amount equal to its gift's price.
 */
(function () {
  'use strict';

  if (!window.fetch || !window.Promise) return;

  var PROP = '_ts_gift';
  var PROP_LABEL = '_ts_gift_label';
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var CACHE_MS = 10 * 60 * 1000;

  var nativeFetch = window.fetch.bind(window);

  /* ---------------- offers ---------------- */

  function toTime(v) {
    if (!v) return null;
    var t = Date.parse(v);
    return isNaN(t) ? null : t;
  }

  var ALL_OFFERS = (window.TS_GIFT_OFFERS || []).filter(function (o) {
    return o && o.code && o.giftVariantId && o.giftProductHandle &&
      ((o.collections && o.collections.length) || (o.productIds && o.productIds.length));
  });
  var OUR_CODES = ALL_OFFERS.map(function (o) { return String(o.code); });

  // Rendered with the page (snippets/ts-gift-cart-state.liquid): for each product already in the
  // cart, the offer codes it qualifies for. Lets the first sync skip the collection lookups.
  var CART_QUAL = window.TS_GIFT_CART_QUAL || {};

  function liveOffers() {
    var now = Date.now();
    return ALL_OFFERS
      .map(function (o, i) { return { o: o, i: i }; })
      .filter(function (x) {
        var s = toTime(x.o.startsAt);
        var e = toTime(x.o.endsAt);
        return (s === null || s <= now) && (e === null || e > now);
      })
      .sort(function (a, b) {
        var pa = a.o.priority == null ? 100 : Number(a.o.priority);
        var pb = b.o.priority == null ? 100 : Number(b.o.priority);
        return pa - pb || a.i - b.i;
      })
      .map(function (x) { return x.o; });
  }

  if (!ALL_OFFERS.length) return;

  /* ---------------- helpers ---------------- */

  function getCookie(name) {
    var parts = document.cookie ? document.cookie.split(';') : [];
    for (var i = 0; i < parts.length; i++) {
      var c = parts[i].trim();
      if (c.indexOf(name + '=') === 0) {
        try { return decodeURIComponent(c.substring(name.length + 1)); } catch (e) { return c.substring(name.length + 1); }
      }
    }
    return null;
  }

  function getJSON(url) {
    return nativeFetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(url + ' ' + r.status); return r.json(); });
  }

  function postJSON(url, body) {
    return nativeFetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.ok; }).catch(function () { return false; });
  }

  function sessionGet(key) {
    try {
      var raw = window.sessionStorage.getItem(key);
      if (!raw) return null;
      var v = JSON.parse(raw);
      return v && Date.now() - v.t < CACHE_MS ? v.d : null;
    } catch (e) { return null; }
  }

  function sessionSet(key, data) {
    try { window.sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: data })); } catch (e) {}
  }

  /* ---------------- catalogue data (cached) ---------------- */

  // Product ids in a collection, via the storefront JSON endpoint (collection must be on Online Store).
  var collectionPromises = {};
  function collectionProductIds(handle) {
    if (collectionPromises[handle]) return collectionPromises[handle];
    var cached = sessionGet('tsgo:c:' + handle);
    if (cached) return (collectionPromises[handle] = Promise.resolve(cached));
    var ids = [];
    function page(n) {
      return getJSON('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + n)
        .then(function (data) {
          var list = (data && data.products) || [];
          list.forEach(function (p) { ids.push(p.id); });
          return list.length === 250 && n < 8 ? page(n + 1) : ids;
        });
    }
    collectionPromises[handle] = page(1).then(function (r) { sessionSet('tsgo:c:' + handle, r); return r; })
      .catch(function () { delete collectionPromises[handle]; return []; });
    return collectionPromises[handle];
  }

  // offer.code -> Set of qualifying product ids
  var membershipPromise = null;
  function membership() {
    if (membershipPromise) return membershipPromise;
    membershipPromise = Promise.all(ALL_OFFERS.map(function (o) {
      return Promise.all((o.collections || []).map(collectionProductIds)).then(function (lists) {
        var set = {};
        (o.productIds || []).forEach(function (id) { set[String(id)] = true; });
        lists.forEach(function (l) { l.forEach(function (id) { set[String(id)] = true; }); });
        return set;
      });
    })).then(function (sets) {
      var byCode = {};
      ALL_OFFERS.forEach(function (o, i) { byCode[o.code] = sets[i]; });
      return byCode;
    });
    return membershipPromise;
  }

  // Product JSON (live availability), cached for this page view.
  var productPromises = {};
  function productJs(handle) {
    if (!productPromises[handle]) {
      productPromises[handle] = getJSON('/products/' + encodeURIComponent(handle) + '.js')
        .catch(function (e) { delete productPromises[handle]; throw e; });
    }
    return productPromises[handle];
  }

  // Gift variants + stock rendered with the page, so picking the gift needs no request.
  // Falls back to the live product JSON when the page didn't include them.
  var embeddedGift = {};
  function giftProductFor(offer) {
    if (offer.giftVariants && offer.giftVariants.length) {
      if (!embeddedGift[offer.code]) {
        embeddedGift[offer.code] = {
          options: offer.giftOptions || [],
          variants: offer.giftVariants.map(function (v) {
            var o = v.o || [];
            return { id: v.id, available: v.available !== false, option1: o[0], option2: o[1], option3: o[2] };
          })
        };
      }
      return Promise.resolve(embeddedGift[offer.code]);
    }
    return productJs(offer.giftProductHandle);
  }

  function findVariant(product, id) {
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === String(id)) return product.variants[i];
    }
    return null;
  }

  function optionValue(product, variant, optionName) {
    var names = product.options || [];
    for (var i = 0; i < names.length; i++) {
      var n = typeof names[i] === 'string' ? names[i] : names[i].name;
      if (String(n).toLowerCase() === String(optionName).toLowerCase()) return variant['option' + (i + 1)];
    }
    return null;
  }

  // Which gift variant a qualifying item gets. qualValue = the item's value for offer.matchOption.
  function pickGiftVariant(offer, giftProduct, qualValue) {
    if (offer.matchOption && qualValue != null) {
      for (var i = 0; i < giftProduct.variants.length; i++) {
        var v = giftProduct.variants[i];
        if (v.available && optionValue(giftProduct, v, offer.matchOption) === qualValue) return v;
      }
    }
    var d = findVariant(giftProduct, offer.giftVariantId);
    return d && d.available ? d : null;
  }

  function lineOptionValue(line, optionName) {
    var opts = line.options_with_values || [];
    for (var i = 0; i < opts.length; i++) {
      if (String(opts[i].name).toLowerCase() === String(optionName).toLowerCase()) return opts[i].value;
    }
    return null;
  }

  function giftProps(offer) {
    var p = {};
    p[PROP] = String(offer.code);
    p[PROP_LABEL] = offer.label || 'Free gift with your order';
    return p;
  }

  /* Works out, for a list of qualifying {value, qty}, which offer applies and which gifts it gives.
     Returns null or { offer, gifts: {variantId: qty} }. */
  function resolveOffer(candidates) {
    // candidates: [{ offer, items: [{ qty, matchValue(optionName) }] }] in priority order
    var i = 0;
    function next() {
      if (i >= candidates.length) return Promise.resolve(null);
      var c = candidates[i++];
      return giftProductFor(c.offer).then(function (gp) {
        var gifts = {};
        var any = false;
        var items = c.offer.perUnit ? c.items : c.items.slice(0, 1);
        items.forEach(function (it) {
          var v = pickGiftVariant(c.offer, gp, c.offer.matchOption ? it.matchValue(c.offer.matchOption) : null);
          if (!v) return;
          var q = c.offer.perUnit ? it.qty : 1;
          gifts[v.id] = (gifts[v.id] || 0) + q;
          any = true;
        });
        return any ? { offer: c.offer, gifts: gifts, giftProduct: gp } : next();
      }, next);
    }
    return next();
  }

  /* ---------------- coupon ---------------- */

  function applyCode(code) {
    if (getCookie('discount_code') === code) return Promise.resolve(false);
    return nativeFetch('/discount/' + encodeURIComponent(code) + '?redirect=%2Fcart.js', { credentials: 'same-origin' })
      .catch(function () {})
      .then(function () {
        if (getCookie('discount_code') !== code) {
          document.cookie = 'discount_code=' + encodeURIComponent(code) + '; path=/; SameSite=Lax';
        }
        return true;
      });
  }

  function clearOurCode() {
    var c = getCookie('discount_code');
    if (!c || OUR_CODES.indexOf(c) === -1) return false;
    document.cookie = 'discount_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    return true;
  }

  /* ---------------- cart sync ---------------- */

  function syncOnce() {
    return getJSON('/cart.js').then(function (cart) {
      var items = cart.items || [];
      var regular = items.filter(function (i) { return !(i.properties && i.properties[PROP]); });
      // Only look up collections when the cart has a product the page didn't pre-qualify.
      var needLookup = regular.some(function (l) { return !CART_QUAL.hasOwnProperty(String(l.product_id)); });
      return (needLookup ? membership() : Promise.resolve(null)).then(function (members) {
        return [cart, members];
      });
    }).then(function (res) {
      var cart = res[0];
      var members = res[1];
      var items = cart.items || [];
      var giftLines = items.filter(function (i) { return i.properties && i.properties[PROP]; });
      var regular = items.filter(function (i) { return !(i.properties && i.properties[PROP]); });

      function qualifies(line, offer) {
        var pid = String(line.product_id);
        if (CART_QUAL.hasOwnProperty(pid)) return CART_QUAL[pid].indexOf(String(offer.code)) !== -1;
        return !!(members && members[offer.code] && members[offer.code][pid]);
      }

      var candidates = [];
      liveOffers().forEach(function (o) {
        var qual = regular.filter(function (l) { return qualifies(l, o); });
        if (!qual.length) return;
        candidates.push({
          offer: o,
          items: qual.map(function (l) {
            return { qty: l.quantity, matchValue: function (name) { return lineOptionValue(l, name); } };
          })
        });
      });

      return resolveOffer(candidates).then(function (chosen) {
        var remaining = {};
        if (chosen) Object.keys(chosen.gifts).forEach(function (k) { remaining[k] = chosen.gifts[k]; });

        var updates = {};
        var needUpdate = false;
        giftLines.forEach(function (l) {
          var mine = chosen && l.properties[PROP] === String(chosen.offer.code) && remaining[l.variant_id] > 0;
          var want = mine ? remaining[l.variant_id] : 0;
          if (mine) remaining[l.variant_id] = 0;
          if (l.quantity !== want) { updates[l.key] = want; needUpdate = true; }
        });

        var adds = Object.keys(remaining)
          .filter(function (k) { return remaining[k] > 0; })
          .map(function (k) { return { id: Number(k), quantity: remaining[k], properties: giftProps(chosen.offer) }; });

        var changed = needUpdate || adds.length > 0;
        var step = needUpdate ? postJSON('/cart/update.js', { updates: updates }) : Promise.resolve(true);
        return step
          .then(function () { return adds.length ? postJSON('/cart/add.js', { items: adds }) : true; })
          .then(function () {
            if (chosen) return applyCode(String(chosen.offer.code)).then(function (c) { return changed || c; });
            var cleared = clearOurCode();
            return changed || (cleared && giftLines.length > 0);
          });
      });
    });
  }

  var running = null;
  var again = false;

  function scheduleSync() {
    if (running) { again = true; return running; }
    running = (function loop(changedSoFar) {
      again = false;
      return syncOnce()
        .catch(function () { return false; })
        .then(function (changed) {
          var total = changedSoFar || changed;
          return again ? loop(total) : total;
        });
    })(false).then(function (changed) {
      running = null;
      var reloading = changed ? refreshUI() : false;
      if (!reloading) clearPending();
      return changed;
    });
    return running;
  }

  /* ---------------- UI refresh after we change the cart ---------------- */

  // Cart page renders with .ts-gift-pending (prices hidden) when a gift is due but not yet added.
  function clearPending() {
    var els = document.querySelectorAll('.ts-gift-pending');
    for (var i = 0; i < els.length; i++) els[i].classList.remove('ts-gift-pending');
  }

  // Returns true when the page is reloading (prices stay hidden until the new page arrives).
  function refreshUI() {
    try { document.dispatchEvent(new CustomEvent('ts:gift-offer:changed')); } catch (e) {}
    if (/^\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return true;
    }
    refreshHeaderCart();
    setTimeout(refreshHeaderCart, 1200); // in case the theme's own refresh lands after ours
    return false;
  }

  function refreshHeaderCart() {
    var ids = ['header', 'header-mobile', 'top-bar'];
    nativeFetch(window.location.pathname + '?sections=' + ids.join(','), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        var html = ids.map(function (id) { return data[id] || ''; }).join('');
        var doc = new DOMParser().parseFromString(html, 'text/html');
        [
          '.cart-dropdown .items-list',
          '.cart-dropdown .cart-info',
          '.cart-dropdown .cart-count',
          '.icon-cart__total',
          '.icon-cart__count',
          '.topbar-item__title',
          '.ts-header__cart'
        ].forEach(function (sel) {
          var fresh = doc.querySelectorAll(sel);
          var live = document.querySelectorAll(sel);
          if (fresh.length && fresh.length === live.length) {
            live.forEach(function (el, i) { el.innerHTML = fresh[i].innerHTML; });
          }
        });
      })
      .catch(function () {});
  }

  /* ---------------- watch every cart change ---------------- */

  // fetch(): callers of a cart mutation get their response only after gifts are in sync,
  // so flows like "add then redirect to /checkout" always include the gift.
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = nativeFetch(input, init);
    if (!CART_MUTATION.test(url)) return p;
    return p.then(function (res) {
      return scheduleSync().then(function () { return res; }, function () { return res; });
    });
  };

  // XHR (jQuery, used by theme.js add-to-cart / mini-cart qty + remove)
  var xhrOpen = XMLHttpRequest.prototype.open;
  var xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__tsGiftUrl = String(url || '');
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (CART_MUTATION.test(this.__tsGiftUrl || '')) {
      this.addEventListener('loadend', function () { scheduleSync(); });
    }
    return xhrSend.apply(this, arguments);
  };

  /* ---------------- Fastrr buttons ---------------- */

  function absImage(product, variant) {
    var img = (variant.featured_image && variant.featured_image.src) ||
      (product.media && product.media[0] && product.media[0].src) ||
      product.featured_image || '';
    return img && img.indexOf('//') === 0 ? 'https:' + img : img;
  }

  function toFastrrItem(product, variant, qty, attrs) {
    return {
      productId: product.id,
      title: product.title,
      variantId: variant.id,
      variantTitle: product.variants.length > 1 ? variant.title : '',
      price: parseFloat((variant.price / 100).toFixed(2)),
      quantity: qty,
      image: absImage(product, variant),
      item_meta_data: { properties: attrs },
      customAttributes: attrs,
      vendor: product.vendor,
      product_type: product.type
    };
  }

  function productHandleFromForm(form) {
    var u = form && form.querySelector('[name="productUrl"]');
    var path = (u && u.value) || window.location.pathname;
    var m = String(path).match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function handOverToFastrr(btn) {
    bypass = true;
    try { btn.click(); } finally { bypass = false; }
  }

  // Resolves to true if it opened checkout with a gift; false if the item doesn't qualify.
  function buyNowWithGift(form, btn) {
    var idInput = form && form.querySelector('input[name="id"]');
    var variantId = idInput ? String(idInput.value) : '';
    var handle = productHandleFromForm(form);
    var qtyInput = form && form.querySelector('input[name="quantity"]');
    var qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
    if (!variantId || !handle) return Promise.resolve(false);

    return Promise.all([productJs(handle), membership()]).then(function (res) {
      var qp = res[0];
      var members = res[1];
      var qv = findVariant(qp, variantId);
      if (!qv || !qv.available) return false;

      var candidates = liveOffers()
        .filter(function (o) { return (members[o.code] || {})[String(qp.id)]; })
        .map(function (o) {
          return { offer: o, items: [{ qty: qty, matchValue: function (name) { return optionValue(qp, qv, name); } }] };
        });
      if (!candidates.length) return false;

      return resolveOffer(candidates).then(function (chosen) {
        if (!chosen) return false;
        return productJs(chosen.offer.giftProductHandle).then(function (gp) { return { chosen: chosen, gp: gp }; });
      }).then(function (r) {
        if (!r) return false;
        var chosen = r.chosen;
        var gp = r.gp;
        var props = giftProps(chosen.offer);
        var products = [toFastrrItem(qp, qv, qty, {})];
        var permalink = [qv.id + ':' + qty];
        Object.keys(chosen.gifts).forEach(function (gid) {
          var gv = findVariant(gp, gid);
          if (!gv) return;
          products.push(toFastrrItem(gp, gv, chosen.gifts[gid], props));
          permalink.push(gv.id + ':' + chosen.gifts[gid]);
        });
        var code = String(chosen.offer.code);
        var fallback = '/cart/' + permalink.join(',') + '?discount=' + encodeURIComponent(code);

        return applyCode(code).then(function () {
          var payload = {
            type: 'cart',
            products: products,
            couponCode: code,
            fallbackUrl: window.location.origin + fallback
          };
          if (window.shiprocketCheckoutEvents && typeof window.shiprocketCheckoutEvents.buyDirect === 'function') {
            window.shiprocketCheckoutEvents.buyDirect(payload);
          } else if (typeof window.shiprocketCheckoutDirectHandler === 'function') {
            window.shiprocketCheckoutDirectHandler(payload);
          } else {
            window.location.href = fallback;
          }
          return true;
        });
      });
    });
  }

  var bypass = false;

  document.addEventListener('click', function (e) {
    if (bypass) return;
    var btn = e.target && e.target.closest ? e.target.closest('.sr-headless-checkout') : null;
    if (!btn) return;
    var box = btn.closest('.shiprocket-headless');
    if (!box) return;
    var type = box.getAttribute('data-type');

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    if (btn.getAttribute('data-ts-busy') === '1') return;
    btn.setAttribute('data-ts-busy', '1');
    function done() { btn.removeAttribute('data-ts-busy'); }

    if (type === 'product' || type === 'product-flits') {
      // qualifying item -> our checkout with gift; anything else -> Fastrr as usual
      buyNowWithGift(box.closest('form'), btn)
        .catch(function () { return false; })
        .then(function (handled) { done(); if (!handled) handOverToFastrr(btn); });
      return;
    }

    // cart / mini-cart: let the cart settle first, then hand over to Fastrr
    scheduleSync().catch(function () {}).then(function () { done(); handOverToFastrr(btn); });
  }, true);

  /* ---------------- start ---------------- */

  function start() {
    // Sync first; warm the collection cache afterwards so it never delays the gift,
    // but is ready for Buy Now and for products added later.
    scheduleSync().then(function () { membership(); });
  }

  /* ---------------- generic "Added to cart" button feedback ---------------- */
  /* The PDP's own Add to Cart button already gets a confirmation via ts-pdp.js (scoped to
   * [data-ts-main]). Every other Add to Cart button on the site (collection grids, homepage
   * product cards, etc.) had no visible feedback at all - the add worked (cart count updated)
   * but the button itself looked like nothing happened. This covers those, generically, by
   * watching the same 'vnd_ajx:crt:success' event the theme's own AJAX cart dispatches after
   * every successful add. */
  var tsFeedbackBtn = null;
  var tsFeedbackResetTimer = null;

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('.btn-add-cart') : null;
    if (!btn || btn.closest('[data-ts-main]') || btn.classList.contains('disabled')) return;
    tsFeedbackBtn = btn;
  }, true);

  document.addEventListener('vnd_ajx:crt:success', function () {
    if (!tsFeedbackBtn) return;
    var btn = tsFeedbackBtn;
    tsFeedbackBtn = null;

    if (btn.dataset.tsOriginalText == null) {
      btn.dataset.tsOriginalText = btn.textContent;
    }
    btn.textContent = 'Added ✓';
    btn.classList.add('ts-add-btn--added');

    if (tsFeedbackResetTimer) clearTimeout(tsFeedbackResetTimer);
    tsFeedbackResetTimer = setTimeout(function () {
      btn.textContent = btn.dataset.tsOriginalText;
      btn.classList.remove('ts-add-btn--added');
    }, 1800);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();